import { useEffect, useRef, useState } from "react";
import {
  useController,
  type Control,
  type FieldValues,
  type Path,
} from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
} from "@components/ui/form";
import {
  LogoClienteFinal,
  type LogoMarcaValue,
} from "@/components/ui/LogoClienteFinal";
import defaultLogo from "@/assets/default-logo.png";

type Props<TForm extends FieldValues> = {
  name: Path<TForm>;
  control: Control<TForm>;
  label?: string;
  description?: string;
  /** Archivo local elegido (aún no subido). */
  file?: File | null;
  onLocalChange?: (file: File | null) => void;
  /** Fondo de la vista previa: el fondo sobre el que se usará el logo. */
  previewBackground?: "light" | "dark";
  /** Muestra "Quitar", que deja el valor del campo en null. */
  clearable?: boolean;
};

/**
 * Campo para subir una imagen (logo). Guarda el File fuera de RHF y
 * muestra la imagen elegida o la guardada sobre el fondo indicado.
 */
export function FileImageField<TForm extends FieldValues>({
  name,
  control,
  label,
  description,
  file,
  onLocalChange,
  previewBackground = "light",
  clearable,
}: Props<TForm>) {
  const { field } = useController({ name, control });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localUrl, setLocalUrl] = useState<string | null>(null);
  const inputId = `file-${String(name).replace(/\W+/g, "-")}`;

  useEffect(() => {
    if (!file) {
      setLocalUrl(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setLocalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const stored = field.value as LogoMarcaValue | null | undefined;
  const hasStored = !!(stored && (stored.llave || stored.url));
  const hasImage = !!file || hasStored;

  const handleClear = () => {
    onLocalChange?.(null);
    field.onChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <FormItem>
      {label && <FormLabel>{label}</FormLabel>}
      {description && <FormDescription>{description}</FormDescription>}
      <FormControl>
        <div className="flex flex-col gap-2">
          <div
            className={`flex h-20 items-center justify-center rounded-md border p-3 ${
              previewBackground === "dark"
                ? "bg-[#141821] border-[#2b313d]"
                : "bg-white border-gray-200"
            }`}
          >
            {localUrl ? (
              <img
                src={localUrl}
                alt="Vista previa del logo"
                className="max-h-full max-w-full object-contain"
              />
            ) : hasStored ? (
              <LogoClienteFinal
                logoMarca={stored}
                defaultSrc={defaultLogo}
                alt="Logo guardado"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span
                className={`text-xs ${
                  previewBackground === "dark" ? "text-gray-400" : "text-gray-500"
                }`}
              >
                Sin logo
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label
              htmlFor={inputId}
              className="cursor-pointer px-4 py-2 rounded bg-gray-100 border border-gray-300 text-gray-700 hover:bg-gray-200 transition flex items-center gap-2"
            >
              <svg
                className="w-5 h-5 text-gray-500"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12"
                />
              </svg>
              <span>{hasImage ? "Cambiar imagen" : "Selecciona una imagen"}</span>
            </label>
            {file && (
              <span className="text-xs text-muted-foreground truncate max-w-[16rem]">
                {file.name}
              </span>
            )}
            {clearable && hasImage && (
              <button
                type="button"
                onClick={handleClear}
                className="text-sm text-red-600 hover:underline"
              >
                Quitar
              </button>
            )}
            <input
              ref={inputRef}
              id={inputId}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => onLocalChange?.(e.target.files?.[0] ?? null)}
            />
          </div>
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  );
}
